import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";

import Loading from "ui/Loading";
import Panel from "ui/Panel";

import QUERY from "./Query.graphql";
import {
  FreezingStatsGridVariables,
  FreezingStatsGrid as FreezingStatsGridType,
  FreezingStatsGrid_getServerFreezingStatsByTable_tableStats,
} from "./types/FreezingStatsGrid";
import Grid, { GridColumn, NoDataGridContainer } from "ui/Grid";
import {
  formatDurationPrecise,
  formatFullTransactionID,
  formatNumber,
  formatTimestampLong,
} from "utils/format";
import moment from "moment";
import { useQueryStringSearch } from "ui/QueryStringSearch";
import { makeFilter } from "utils/filter";
import Popover from "ui/Popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { useRoutes } from "utils/routes";
import { Link } from "react-router-dom";
import Tip from "ui/Tip";
import { faClock } from "@fortawesome/pro-regular-svg-icons";

type FreezingStatsGridItemType =
  FreezingStatsGrid_getServerFreezingStatsByTable_tableStats & {
    lastAnyVacuumAt: number | undefined;
  };

const FreezingStatsGrid: React.FunctionComponent<{
  serverId: string;
  databaseId?: string;
}> = ({ serverId, databaseId }) => {
  const { databaseTableVacuum } = useRoutes();
  const [xidSearchTerm, XidSearchInput] = useQueryStringSearch("qx");
  const [mxidSearchTerm, MxidSearchInput] = useQueryStringSearch("qm");
  const { data, loading, error } = useQuery<
    FreezingStatsGridType,
    FreezingStatsGridVariables
  >(QUERY, {
    variables: {
      serverId,
      databaseId,
    },
  });
  const tableStats = useMemo(() => {
    if (!data?.getServerFreezingStatsByTable) {
      return undefined;
    }
    return data.getServerFreezingStatsByTable.tableStats.map((stat) => {
      return {
        ...stat,
        lastAnyVacuumAt: lastAnyVacuumAt(stat),
      };
    });
  }, [data]);

  if (loading || error) {
    return (
      <Panel title="Oldest Unfrozen Transaction ID Age Stats by Table">
        <Loading error={!!error} />
      </Panel>
    );
  }

  const stats = data.getServerFreezingStatsByTable;

  if (!stats) {
    return (
      <Panel title="Oldest Unfrozen Transaction ID Age Stats by Table">
        <NoDataGridContainer>
          Freezing stats data is not available. Please make sure to use the
          collector version v0.48.0 or above.
        </NoDataGridContainer>
      </Panel>
    );
  }

  const secondaryTitleTimestamp = (
    <span className="mr-[10px] text-[#595959]">
      {moment.unix(stats.lastCollectedAt).format("ll LTS z")}
    </span>
  );

  const xidData = tableStats
    .filter((xid) => !!xid.frozenxidAge)
    .filter(makeFilter(xidSearchTerm, "tableName", "datname"));

  const mxidData = tableStats
    .filter((mxid) => !!mxid.minmxidAge)
    .filter(makeFilter(mxidSearchTerm, "tableName", "datname"));

  const dbColumnWidth = databaseId == null ? "160px" : false;
  const xidColumns: GridColumn<
    FreezingStatsGridItemType,
    keyof FreezingStatsGridItemType
  >[] = [
    {
      field: "datname",
      header: "Database",
      width: dbColumnWidth,
    },
    {
      field: "schemaName",
      header: "Schema",
      width: "160px",
    },
    {
      field: "tableName",
      header: "Table",
      width: "1fr",
      renderer: function TableNameCell({
        fieldData,
        rowData,
      }: {
        fieldData: string;
        rowData: FreezingStatsGridItemType;
      }) {
        return (
          <Link
            to={databaseTableVacuum(
              rowData.databaseId.toString(),
              rowData.tableId.toString(),
            )}
          >
            {fieldData}
          </Link>
        );
      },
    },
    {
      field: "frozenxidAge",
      header: "Oldest Unfrozen XID Age",
      width: "300px",
      renderer: function FrozenXidAgeCell({
        fieldData,
        rowData,
      }: {
        fieldData: number;
        rowData: FreezingStatsGridItemType;
      }) {
        const aboveAutoVacuum = fieldData > stats.autovacuumFreezeMaxAge;
        const additionalInfo = [
          {
            title: "Oldest Unfrozen XID",
            value: formatFullTransactionID(rowData.frozenXid),
          },
          {
            title: "Oldest Unfrozen XID was assigned at",
            value: rowData.frozenxidAssignedAt
              ? `${formatDurationPrecise(
                  (stats.lastCollectedAt - rowData.frozenxidAssignedAt) * 1000,
                )} ago`
              : "n/a",
          },
        ];
        return (
          <AgeCell
            aboveAutoVacuum={aboveAutoVacuum}
            ageValue={formatNumber(fieldData)}
            additionalInfo={additionalInfo}
          />
        );
      },
      style: "number",
      defaultSortOrder: "DESC",
      tip: "The age of the oldest unfrozen XID, in transactions. How many transactions happened since the oldest unfrozen XID.",
    },
    {
      field: "lastAnyVacuumAt",
      header: "Last Vacuum At",
      width: "180px",
      nullValue: "n/a",
      renderer: function LastVacuumAtCell({ fieldData }) {
        return formatTimestampLong(moment.unix(fieldData));
      },
      tip: "Last time at which this table was vacuumed manually or by the autovacuum daemon, whichever is later.",
    },
  ];

  const mxidColumns: GridColumn<
    FreezingStatsGridItemType,
    keyof FreezingStatsGridItemType
  >[] = [
    {
      field: "datname",
      header: "Database",
      width: dbColumnWidth,
    },
    {
      field: "schemaName",
      header: "Schema",
      width: "160px",
    },
    {
      field: "tableName",
      header: "Table",
      width: "1fr",
      renderer: function TableNameCell({ fieldData, rowData }) {
        return (
          <Link
            to={databaseTableVacuum(
              rowData.databaseId.toString(),
              rowData.tableId.toString(),
            )}
          >
            {fieldData}
          </Link>
        );
      },
    },
    {
      field: "minmxidAge",
      header: "Oldest Unfrozen MXID Age",
      width: "300px",
      renderer: function MinmxidAgeCell({ fieldData, rowData }) {
        const aboveAutoVacuum =
          fieldData > stats.autovacuumMultixactFreezeMaxAge;

        const additionalInfo = [
          {
            title: "Oldest Unfrozen MXID",
            value: formatNumber(rowData.minMxid),
          },
          {
            title: "Oldest Unfrozen MXID was assigned at",
            value: rowData.minmxidAssignedAt
              ? `${formatDurationPrecise(
                  (stats.lastCollectedAt - rowData.minmxidAssignedAt) * 1000,
                )} ago`
              : "n/a",
          },
        ];
        return (
          <AgeCell
            aboveAutoVacuum={aboveAutoVacuum}
            ageValue={formatNumber(fieldData)}
            additionalInfo={additionalInfo}
          />
        );
      },
      style: "number",
      defaultSortOrder: "DESC",
      tip: "The age of the oldest unfrozen MXID, in transactions. How many multi transactions happened since the oldest unfrozen MXID.",
    },
    {
      field: "lastAnyVacuumAt",
      header: "Last Vacuum At",
      width: "180px",
      nullValue: "n/a",
      renderer: function LastVacuumAtCell({ fieldData }) {
        return formatTimestampLong(moment.unix(fieldData));
      },
      tip: "Last time at which this table was vacuumed manually or by the autovacuum daemon, whichever is later.",
    },
  ];

  return (
    <>
      <Panel
        title={`Oldest Unfrozen Transaction ID Age Stats by Table (${xidData.length})`}
        secondaryTitle={
          <>
            {secondaryTitleTimestamp}
            <XidSearchInput />
          </>
        }
      >
        {xidData.length > 0 ? (
          <Grid
            data={xidData}
            defaultSortBy="frozenxidAge"
            columns={xidColumns}
            pageSize={10}
          />
        ) : (
          <NoDataGridContainer>
            No Transaction ID information for this server{" "}
            <Tip content="Some Transaction ID information is not collected if the server is not primary." />
          </NoDataGridContainer>
        )}
      </Panel>
      <Panel
        title={`Oldest Unfrozen Multixact ID Age Stats by Table (${mxidData.length})`}
        secondaryTitle={
          <>
            {secondaryTitleTimestamp}
            <MxidSearchInput />
          </>
        }
      >
        {mxidData.length > 0 ? (
          <Grid
            data={mxidData}
            defaultSortBy="minmxidAge"
            columns={mxidColumns}
            pageSize={10}
          />
        ) : (
          <NoDataGridContainer>
            No Multixact ID information for this server{" "}
            <Tip content="Multixact IDs are used to support row locking by multiple transactions. It is normal to have no data if your database is not using multixact IDs." />
          </NoDataGridContainer>
        )}
      </Panel>
    </>
  );
};

interface AdditionalInfo {
  title: string;
  value: string;
}

const AgeCell: React.FunctionComponent<{
  aboveAutoVacuum: boolean;
  ageValue: string;
  additionalInfo?: AdditionalInfo[];
}> = ({ aboveAutoVacuum, ageValue, additionalInfo }) => {
  const additionalInfoContent = additionalInfo.map((info, i) => {
    return (
      <div className="font-sans" key={i}>
        <div className="font-medium">{info.title}:</div>
        <div>{info.value}</div>
      </div>
    );
  });
  return (
    <>
      {aboveAutoVacuum && (
        <Popover
          popupClassName="font-sans"
          content="Anti-wraparound autovacuum expected since age is above threshold"
        >
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="mr-1"
            color="#C22426"
          />
        </Popover>
      )}
      <span className={aboveAutoVacuum ? "text-[#C22426]" : ""}>
        {ageValue}
      </span>
      {additionalInfo && (
        <Popover content={additionalInfoContent} popupClassName="font-sans">
          <FontAwesomeIcon icon={faClock} className="ml-1" color="#777777" />
        </Popover>
      )}
    </>
  );
};

function lastAnyVacuumAt(
  rowData: Pick<FreezingStatsGridItemType, "lastVacuumAt" | "lastAutovacuumAt">,
): number | undefined {
  if (rowData.lastVacuumAt == null && rowData.lastAutovacuumAt == null) {
    return undefined;
  }

  return Math.max(rowData.lastVacuumAt ?? 0, rowData.lastAutovacuumAt ?? 0);
}

export default FreezingStatsGrid;
